import { Button, Card, Flex, Input, Modal, Space, Table, TableColumnsType, Tag, Typography } from "antd";
import { SortOrder } from "antd/es/table/interface";
import {
  Approved,
  TJobSummary,
  VoiceFileStatus,
  VoiceFileType,
  VoiceStatFlag
} from "api/services/models/types";
import { useLayoutContext } from "components/MainContainer";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useEffect, useState } from "react";
import { Sheet } from "react-modal-sheet";
import VoiceFileNameColumn from "./VoiceFileAudioColumn";
import { useAudioPlayerContext } from "./AudioPlayerContext";

dayjs.extend(duration);

type TJobSummaryGroupByProvider = {
  providerName: string;
  totalDictations: number;
  totalDictationsAddendums: number;
  completed: number;
  completedAddendums: number;
  pendingPr: number;
  pendingPrAddendums: number;
  pendingTr: number;
  pendingTrAddendums: number;
  blankOrInvalid: number;
  pendingApproval: number;
  pendingApprovalAddendums: number;
  stat: number;
  statAddendums: number;
};

type TJobSummaryDetail = {
  vfid: number;
  voiceFileName: string;
  duration: number;
  dateOfDictation: string;
  deliveredTime?: string;
  patientName?: string;
  mrn?: string;
  dateOfService?: string;
  reportType?: string;
  status: string;
};

const hasTranscripts = (summary: TJobSummary) => (summary.transcripts?.length ?? 0) > 0;
const isComplete = (summary: TJobSummary) =>
  summary.voiceFileStatus === VoiceFileStatus.Completed;
const isValid = (summary: TJobSummary) => summary.type === VoiceFileType.Valid;
const isBlank = (summary: TJobSummary) =>
  [VoiceFileType.BlankPerDistributor, VoiceFileType.BlankPerMT].includes(summary.type);
const isAddendum = (summary: TJobSummary) => summary.type === VoiceFileType.Addendum;

const isCompleted = (summary: TJobSummary) =>
  isComplete(summary) && hasTranscripts(summary) ? 1 : 0;

const isPendingPr = (summary: TJobSummary) =>
  isComplete(summary) && isValid(summary) && !hasTranscripts(summary) ? 1 : 0;

const isPendingTr = (summary: TJobSummary) =>
  !isComplete(summary) && isValid(summary) ? 1 : 0;

const blankOrInvalid = (summary: TJobSummary) =>
  !isComplete(summary) && !isValid(summary) && !isAddendum(summary) ? 1 : 0;

const pendingApproval = (summary: TJobSummary) =>
  isComplete(summary) &&
  hasTranscripts(summary) &&
  summary.transcripts?.some(t => t.approved === Approved.No)
    ? 1
    : 0;

const isSTAT = (summary: TJobSummary) => (summary.statFlag === VoiceStatFlag.Stat ? 1 : 0);

const groupByProviderName = (data: TJobSummary[]): TJobSummaryGroupByProvider[] => {
  const grouped = data.reduce((op, s) => {
    const isAddendum = s.type === VoiceFileType.Addendum;
    const providerName = s.provider?.providerName ?? "UNKNOWN";
    op[providerName] = {
      totalDictations: (op[providerName]?.totalDictations ?? 0) + 1,
      totalDictationsAddendums:
        (op[providerName]?.totalDictationsAddendums ?? 0) + (isAddendum ? 1 : 0),
      completed: (op[providerName]?.completed ?? 0) + isCompleted(s),
      completedAddendums:
        (op[providerName]?.completedAddendums ?? 0) + (isAddendum ? isCompleted(s) : 0),
      pendingPr: (op[providerName]?.pendingPr ?? 0) + isPendingPr(s),
      pendingPrAddendums:
        (op[providerName]?.pendingPrAddendums ?? 0) + (isAddendum ? isPendingPr(s) : 0),
      pendingTr: (op[providerName]?.pendingTr ?? 0) + isPendingTr(s),
      pendingTrAddendums:
        (op[providerName]?.pendingTrAddendums ?? 0) + (isAddendum ? isPendingTr(s) : 0),
      blankOrInvalid: (op[providerName]?.blankOrInvalid ?? 0) + blankOrInvalid(s),
      pendingApproval: (op[providerName]?.pendingApproval ?? 0) + pendingApproval(s),
      pendingApprovalAddendums:
        (op[providerName]?.pendingApprovalAddendums ?? 0) +
        (isAddendum ? pendingApproval(s) : 0),
      stat: (op[providerName]?.stat ?? 0) + isSTAT(s),
      statAddendums: (op[providerName]?.statAddendums ?? 0) + (isAddendum ? isSTAT(s) : 0)
    };
    return op;
  }, {} as Record<string, Partial<TJobSummaryGroupByProvider>>);
  return Object.entries(grouped)
    .map(
      ([name, data]) =>
        ({
          providerName: name,
          ...data
        } as TJobSummaryGroupByProvider)
    )
    .toSorted(providerNameSorter);
};
const providerNameSorter = (
  s1: TJobSummaryGroupByProvider,
  s2: TJobSummaryGroupByProvider,
  sortOrder?: SortOrder
) => {
  if (s1.providerName === "UNKNOWN") {
    return sortOrder === "descend" ? 1 : -1;
  }
  if (s2.providerName === "UNKNOWN") {
    return sortOrder === "descend" ? -1 : 1;
  }
  return s1.providerName.localeCompare(s2.providerName);
};

const columns: TableColumnsType<TJobSummaryGroupByProvider> = [
  {
    title: "Provider Name",
    dataIndex: "providerName",
    key: "providerName",
    sorter: providerNameSorter,
    render: text => (
      <span
        style={{
          color: "#1868db"
        }}>
        {text}
      </span>
    )
  },
  {
    // title: "Dictations / Addendums",
    title: "Dictations",
    key: "totalDictations",
    render: (_, record) => (
      <span>
        {/* {record.totalDictations}-{record.totalDictationsAddendums} /{" "}
        {record.totalDictationsAddendums} */}
        {record.totalDictations}
      </span>
    )
  },
  {
    // title: "Completed Jobs / Addendums",
    title: "Completed Jobs",
    key: "completed",
    render: (_, record) => (
      <span>
        {/* {record.completed}-{record.completedAddendums} / {record.completedAddendums} */}
        {record.completed}
      </span>
    )
  },
  {
    // title: "Pending Proofreading / Addendums",
    title: "Pending Proofreading",
    key: "pendingPr",
    render: (_, record) => (
      <span>
        {/* {record.pendingPr}- {record.pendingPrAddendums} / {record.pendingPrAddendums} */}
        {record.pendingPr}
      </span>
    )
  },
  {
    // title: "Pending Transcription / Addendums",
    title: "Pending Transcription",
    key: "pendingTr",
    render: (_, record) => (
      <span>
        {/* {record.pendingTr}-{record.pendingTrAddendums} / {record.pendingTrAddendums} */}
        {record.pendingTr}
      </span>
    )
  },
  {
    title: "Blank / Invalid",
    dataIndex: "blankOrInvalid",
    key: "blankOrInvalid"
  },
  {
    // title: "Pending Approval / Addendums",
    title: "Pending Approval",
    key: "pendingApproval",
    render: (_, record) => (
      <span>
        {/* {record.pendingApproval}- {record.pendingApprovalAddendums} /{" "}
        {record.pendingApprovalAddendums} */}
        {record.pendingApproval}
      </span>
    )
  },
  {
    // title: "STAT / Addendums",
    title: "STAT",
    key: "stat",
    render: (_, record) => (
      <span>
        {/* {record.stat}- {record.statAddendums} / {record.statAddendums} */}
        {record.stat}
      </span>
    )
  }
];

const jobStatus = (summary: TJobSummary) => {
  if (isComplete(summary)) {
    return "Pending Proofreading";
  }
  if (isValid(summary)) {
    return "Pending Transcription";
  }
  if (isBlank(summary)) {
    return "Blank";
  }
  if (isAddendum(summary)) {
    return "Addendum";
  }
  return "Invalid";
};

const getTag = (status: string) => {
  let chipColor = "default";
  let chipBorderColor = "default";
  let chipBGColor = "default";
  let chipText = status ?? "";

  if (status === "Completed") {
    chipBGColor = "#00BE1E33";
    chipBorderColor = "#00BE1E4D";
    chipColor = "#00BE1E";
  }

  if (status === "Pending Proofreading") {
    chipBGColor = "#76D6FF33";
    chipBorderColor = "#76D6FF4D";
    chipColor = "#76D6FF";
  }

  if (status === "Pending Transcription") {
    chipBGColor = "#77777733";
    chipBorderColor = "#7777774D";
    chipColor = "#777777";
  }

  if (status === "Blank" || status === "Invalid" || status === "Addendum") {
    chipBGColor = "#FF7A0033";
    chipBorderColor = "#FF7A004D";
    chipColor = "#FF7A00";
  }

  return (
    <Tag
      color={chipBGColor}
      style={{
        borderRadius: ".5rem",
        border: `1px solid ${chipBorderColor}`,
        padding: ".25rem .625rem",
        color: `${chipColor}`
      }}>
      {chipText}
    </Tag>
  );
};

const mapToDetail = (summary: TJobSummary): TJobSummaryDetail[] => {
  if (summary.transcripts?.length ?? 0) {
    return summary.transcripts.map(t => ({
      voiceFileName: summary.voiceFileName,
      vfid: summary.vfId,
      duration: summary.duration,
      dateOfDictation: summary.dateOfDictation,
      deliveredTime: t.deliveredTime,
      patientName: t.patientName,
      mrn: t.mrn,
      dateOfService: t.dateOfService,
      reportType: t.reportType,
      status: isComplete(summary) ? "Completed" : "Pending Proofreading"
    }));
  } else {
    return [
      {
        voiceFileName: summary.voiceFileName,
        duration: summary.duration,
        dateOfDictation: summary.dateOfDictation,
        status: jobStatus(summary),
        vfid: summary.vfId
      }
    ];
  }
};

const JobSummaryGrid = ({ data }: { data: TJobSummary[] }) => {

  const groupedData = groupByProviderName(data);
  const { isSmallScreen } = useLayoutContext();
  const [open, setOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<TJobSummaryGroupByProvider>();
  const { currentAudio, setCurrentAudio, setCurrentlyPlayingId } = useAudioPlayerContext();

  useEffect(() => {
    if (!open && currentAudio) {
      currentAudio?.pause();
      setCurrentAudio(null);
      setCurrentlyPlayingId(null);
    }
  }, [open]);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", !isSmallScreen);
  }, [isSmallScreen]);

  const expandedColumns: TableColumnsType<TJobSummaryDetail> = [
    {
      title: "Voice File Name",
      dataIndex: "voiceFileName",
      key: "voiceFileName",
      width: 240,
      render: (text, record) => (
        <Flex align="center">
          <VoiceFileNameColumn text={text} record={record} />
          <Typography.Paragraph
            copyable={{ tooltips: null }}
            >
            {text}
          </Typography.Paragraph>
        </Flex>
      ),
      sorter: (s1, s2) => s1.voiceFileName.localeCompare(s2.voiceFileName),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
        <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()} role="none">
          <Input
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              onClick={() => clearFilters?.({ closeDropdown: true, confirm: true })}
              size="small"
              style={{ width: 90 }}>
              Reset
            </Button>
            <Button
              type="primary"
              onClick={() => confirm({ closeDropdown: true })}
              size="small"
              style={{ width: 90 }}>
              Search
            </Button>
          </Space>
        </div>
      ),
      filterDropdownProps: {
        placement: "bottom",
      },
      filterSearch: true,
      onFilter: (value, record) =>
        record.voiceFileName.toLowerCase().includes((value as string).toLowerCase())
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: d =>
        d < 60
          ? dayjs.duration(d, "seconds").format("s[S]")
          : d < 3600
          ? dayjs.duration(d, "seconds").format("m[M]:s[S]")
          : dayjs.duration(d, "seconds").format("H[H]:m[M]:s[S]")
    },
    {
      title: "Date of Dictation (EST)",
      dataIndex: "dateOfDictation",
      key: "dateOfDictation",
      width: 180,
      defaultSortOrder: "descend",
      render: d => dayjs(d).format("MM-DD-YY HH:mm:ss"),
      sorter: (a: any, b: any) => new Date(a.dateOfDictation).getTime() - new Date(b.dateOfDictation).getTime()
    },
    {
      title: "Date of Delivery (EST)",
      dataIndex: "deliveredTime",
      key: "deliveredTime",
      width: 180,
      render: d => (d ? dayjs(d).format("MM-DD-YY HH:mm:ss") : ""),
      sorter: (a, b) => {
        const dateA = a.deliveredTime ? dayjs(a.deliveredTime).valueOf() : 0;
        const dateB = b.deliveredTime ? dayjs(b.deliveredTime).valueOf() : 0;
        return dateA - dateB;
      }
    },
    {
      title: "TAT",
      dataIndex: "deliveredTime",
      key: "tat",
      render: (_, s) =>
        s.deliveredTime
          ? dayjs
              .duration(dayjs(s.deliveredTime).diff(dayjs(s.dateOfDictation)), "milliseconds")
              .format("H[H]:m[M]")
          : ""
    },
    {
      title: "Patient Name",
      dataIndex: "patientName",
      key: "patientName"
    },
    {
      title: "MRN",
      dataIndex: "mrn",
      key: "mrn"
    },
    {
      title: "Date of Service",
      dataIndex: "dateOfService",
      key: "dateOfService",
      render: d => (d ? dayjs(d).format("MM-DD-YY") : "")
    },
    {
      title: "Report Type",
      dataIndex: "reportType",
      key: "reportType"
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 166,
      render: (status: string) => getTag(status)
    }
  ];

  const handleCombinedClick = (item: TJobSummaryGroupByProvider | undefined) => {
    setCurrentItem(item);
    setOpen(true);
  };
  interface ProviderCardProps {
    item: TJobSummaryGroupByProvider | undefined;
    cardStyle?: React.CSSProperties;
    onClick?: () => void;
  }

  interface ProviderDetailsCardProps {
    item: TJobSummaryDetail | undefined;
    cardStyle?: React.CSSProperties;
    onClick?: () => void;
  }

  const ProviderCard: React.FC<ProviderCardProps> = ({ item, cardStyle = {}, onClick }) => {
    return (
      <Card
        key={item?.providerName}
        style={{ width: "100%", backgroundColor: "#FFF", ...cardStyle }}
        onClick={onClick}>
        <Flex gap="middle" align="end" justify="space-between" style={{ marginBottom: "16px" }}>
          <Typography.Title style={{ fontSize: "1rem" }}>
            {item?.providerName}
          </Typography.Title>
        </Flex>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: "16px",
            paddingTop: "16px"
          }}>
          {[
            {
              label: "Dictations",
              value: `${item?.totalDictations}`
            },
            {
              label: "Completed Jobs",
              value: `${item?.completed}`
            },
            {
              label: "Pending Proofreading",
              value: `${item?.pendingPr}`
            },
            {
              label: "Pending Transcription",
              value: `${item?.pendingTr}`
            },
            { label: "Invalid", 
              value: item?.blankOrInvalid },
            {
              label: "Pending Approval",
              value: `${item?.pendingApproval}`
            },
            { label: "Stat", 
              value: `${item?.stat}` 
            }
          ].map(({ label, value }) => (
            <div key={label} style={{ display: "flex" }}>
              <span className="font-777" style={{ whiteSpace: "nowrap" }}>
                {label}:{" "}
              </span>
              <span className="font-1212 font-1212-mobile" style={{ whiteSpace: "nowrap" }}>
                {value}
              </span>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  const ProviderDetailsCard: React.FC<ProviderDetailsCardProps> = ({
    item,
    cardStyle = {},
    onClick
  }) => {
    return (
      <Card
        key={item?.voiceFileName}
        style={{ width: "100%", backgroundColor: "#FFF", ...cardStyle }}
        onClick={onClick}>
        <Flex gap="middle" align="center" justify="space-between" style={{ marginBottom: "16px" }}>
          <VoiceFileNameColumn text={item?.voiceFileName ?? ""} record={item || null} />
          <Typography.Title style={{ fontSize: "1rem" }} className="font-1212">
            {item?.voiceFileName}
          </Typography.Title>
          <Space size="small" direction="vertical" style={{ margin: 0 }}>
            {getTag(item?.status ?? "")}
          </Space>
        </Flex>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "16px",
            paddingTop: "16px"
          }}>
          {[
            { label: "Patient name", 
              value: item?.patientName 
            },
            { label: "MRN", value: item?.mrn },
            {
              label: "TAT",
              value: item?.deliveredTime
                ? dayjs
                    .duration(
                      dayjs(item?.deliveredTime).diff(dayjs(item?.dateOfDictation)),
                      "milliseconds"
                    )
                    .format("H[H]:m[M]")
                : ""
            },
            {
              label: "Duration",
              value: (item?.duration ?? 0) < 60
                ? dayjs.duration((item?.duration ?? 0), "seconds").format("s[S]")
                : (item?.duration ?? 0) < 3600
                  ? dayjs.duration((item?.duration ?? 0), "seconds").format("m[M]:s[S]")
                  : dayjs.duration((item?.duration ?? 0), "seconds").format("H[H]:m[M]:s[S]")
            },
            {
              label: "Dictation date",
              value: dayjs(item?.dateOfDictation).format("MM/DD/YYYY")
            },
            {
              label: "Date of service",
              value: dayjs(item?.dateOfService).format("MM/DD/YYYY")
            },
            {
              label: "Date of delivery",
              value: dayjs(item?.deliveredTime).format("MM/DD/YYYY")
            },
            { label: "Report type", 
              value: item?.reportType 
            }
          ].map(({ label, value }) => (
            <div key={label} style={{ display: "flex" }}>
              <span className="font-777" style={{ whiteSpace: "nowrap" }}>
                {label}:{" "}
              </span>
              <span className="font-1212 font-1212-mobile" style={{ whiteSpace: "nowrap" }}>
                {value}
              </span>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  return (
    <>
      {!isSmallScreen ? (
        <>
        <Table<TJobSummaryGroupByProvider>
          columns={columns}
          dataSource={groupedData}
          style={{ border: "none" }}
          pagination={false}
          rowKey={"providerName"}
          className="table-style"
          onRow={(record) => {
            return {
              onClick: () => {
                handleCombinedClick(record);
              },
            };
          }}
          components={{
            body: {
              cell: (props: any) => (
                <td
                  {...props}
                  style={{
                    fontWeight: 500,
                  }}
                />
              )
            }
          }}
        />
        
          <Modal
            open={open}
            onCancel={() => setOpen(false)}
            width="95%"
            footer={null}
          >
            <Card
              className="card-style"
              styles={{ body: { padding: "0" } }}
              style={{ border: "none", backgroundColor: "#FAFAFA", padding: "1rem", margin: "20px" }}>
              <ProviderCard
                item={currentItem}
                cardStyle={{ backgroundColor: "#3A86FF33", marginBottom: "1.25rem" }}
              />
              <Table<TJobSummaryDetail>
                columns={expandedColumns}
                sticky
                pagination={false}
                className="table-style"
                rowKey={"voiceFileName"}
                scroll={{ x: true }}
                style={{ maxHeight: "50vh", overflowY: "auto" }}
                components={{
                  body: {
                    cell: (props: any) => (
                      <td
                        {...props}
                        style={{
                          borderBottomWidth: "1px",
                          borderBottomColor: "#f0f0f0",
                          borderBottomStyle: "solid",
                          fontWeight: "500",
                        }}
                      />
                    )
                  }
                }}
                dataSource={data
                  .filter(
                    s =>
                      currentItem?.providerName ===
                      (s.provider == null ? "UNKNOWN" : s.provider.providerName)
                  )
                  .flatMap(s => mapToDetail(s))}
              />
            </Card>
          </Modal>
          </>
      ) : (
        <>
          <Flex
            wrap="wrap"
            gap="middle"
            justify="space-between"
            style={{ marginTop: "1.75rem" }}>
            {(() => {
              if (groupedData.length === 0) {
                return (
                  <Typography.Title level={4} style={{ textAlign: "center", width: "100%" }}>
                    No Jobs Found
                  </Typography.Title>
                );
              }

              return groupedData.map(item => (
                <ProviderCard item={item} onClick={() => handleCombinedClick(item)} />
              ));
            })()}
          </Flex>
          <Sheet isOpen={open} onClose={() => setOpen(false)}>
            <Sheet.Container>
              <Sheet.Header />
              <Sheet.Content style={{ padding: "1.25rem" }}>
                <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  <ProviderCard
                    item={currentItem}
                    cardStyle={{ backgroundColor: "#3A86FF33" }}
                  />
                  <span
                    style={{ marginTop: "1.25rem", fontSize: "0.875rem" }}
                    className="font-1212">
                    Details Of Provider : {currentItem?.providerName}
                  </span>

                  <div
                    style={{
                      flexGrow: 1,
                      overflowY: "auto",
                      marginTop: "1rem"
                    }}>
                    {(() => {
                      if (
                        data.filter(
                          s =>
                            currentItem?.providerName ===
                            (s.provider == null ? "UNKNOWN" : s.provider.providerName)
                        ).length === 0
                      ) {
                        return (
                          <Typography.Title
                            level={4}
                            style={{ textAlign: "center", width: "100%" }}>
                            No Jobs Found
                          </Typography.Title>
                        );
                      }

                      return data
                        .filter(
                          s =>
                            currentItem?.providerName ===
                            (s.provider == null ? "UNKNOWN" : s.provider.providerName)
                        )
                        .flatMap(s => mapToDetail(s))
                        .map(item => <ProviderDetailsCard item={item} />);
                    })()}
                  </div>
                </div>
              </Sheet.Content>
            </Sheet.Container>
            <Sheet.Backdrop />
          </Sheet>
        </>
      )}
    </>
  );
};
export default JobSummaryGrid;
